<template>
    <div>
        <v-alert v-if="!props.isFirstVersion" type="info" variant="tonal" class="mb-4"> By clicking Save, you will create a new version of this property. It will apply to all upcoming inventories, starting from the year selected in Applicable From. The previous version will be archived as a historical record. </v-alert>
        <v-row>
            <v-col v-if="!props.isFirstVersion" cols="12" sm="6">
                <Autocomplete
                    v-model:form="form"
                    :items="yearItems"
                    :loading="fetchingYears"
                    :selected-items="form.effective_year"
                    :chips="false"
                    label="Applicable From *"
                    field-name="effective_year"
                    @update:selected-items="
                        (v) => {
                            form.effective_year = ((v as number | null) ?? '') as unknown as number;
                        }
                    "
                />
            </v-col>
            <v-col v-if="!props.isFirstVersion" cols="12" sm="6">
                <ValidatableTextField v-model:form="form" label="Reason for Change" :field="'reason_for_change'" />
            </v-col>
            <v-col cols="12" sm="6">
                <ValidatableTextField v-model:form="form" :display-formatter="(value: string) => formatArea(value)" :min="0" type="number" label="Total Area (ha) *" :field="'property_area_ha'" />
            </v-col>
            <v-col cols="12" sm="6">
                <PartitionAreaSummary :total="form.property_area_ha" :partitioned="partitionedArea" :unpartitioned="unpartitionedArea" />
            </v-col>
        </v-row>
        <PartitionRowEditor v-model:form="form" array-key="version_partitions" :landuse-types="landuseTypes" :fetching-landuse-types="fetchingLanduseTypes" />
    </div>
</template>

<script setup lang="ts">
import { useInjectSubmitForm } from '@shared/composables/action-dialog/useInjectSubmitForm';
import { type IPropertyVersion, VersionStatus } from '@app/land-hold/property/models/PropertyVersion';
import type { IProperty } from '@app/land-hold/property/models/Property';
import { usePropertyRepository } from '@app/land-hold/property/repositories/usePropertyRepository';
import { useLanduseTypes } from '@app/land-hold/land-use-type/composables/useLanduseTypes';
import { useAvailableVersionYears } from '@app/land-hold/property/composables/useAvailableVersionYears';
import Autocomplete from '@shared/components/atoms/Autocomplete.vue';
import type { SingleObjectResponse } from '@shared/types/ApiResponse';
import { formatArea } from '@shared/utils/formatArea.ts';

const props = defineProps({
    item: {
        type: Object as PropType<IPropertyVersion>,
        required: true,
    },
    property: {
        type: Object as PropType<SingleObjectResponse<IProperty>>,
        required: true,
    },
    isFirstVersion: {
        type: Boolean,
        default: false,
    },
});

const activeVersion = computed(() => {
    const versions = props.property.data.versions?.data ?? [];
    return versions.find((v) => v.status === VersionStatus.Active);
});

const baseInit = {
    ...props.item,
    property_id: props.property.data.id,
    property_area_ha: activeVersion.value?.property_area_ha ?? props.property.data.property_area_ha ?? '',
    version_partitions: (activeVersion.value?.version_partitions.data ?? []).map((p) => ({
        partition_area_ha: Number(p.partition_area_ha),
        landuse_type_id: p.landuse_type_id,
    })),
};
const formInit = props.isFirstVersion ? baseInit : { ...baseInit, effective_year: '' as unknown as number, reason_for_change: '' };

const store = usePropertyRepository().command.storeVersion(props.property.data.id, formInit as IPropertyVersion & { version_partitions: { partition_area_ha: number | string; landuse_type_id: string }[] });
const form = store.form;

const partitionedArea = computed(() => (form.version_partitions ?? []).reduce((sum, p) => sum + (Number(p.partition_area_ha) || 0), 0));
const unpartitionedArea = computed(() => (Number(form.property_area_ha) || 0) - partitionedArea.value);

useInjectSubmitForm(store.execute);

const { landuseTypes, fetchingLanduseTypes } = useLanduseTypes();
const { years, fetchingYears } = useAvailableVersionYears(props.property.data.id);
const yearItems = computed(() => years.value.map((y) => ({ id: y, name: String(y) })));
</script>
